import React, { useState } from 'react';
import { Mail, Send } from 'lucide-react';

interface EmailComposeModalProps {
  type: 'invitation' | 'reminder';
  feedbackTitle: string;
  deadline?: string;
  savedSubject?: string;
  savedBody?: string;
  onSend: (subject: string, body: string) => void;
  onClose: () => void;
}

const DEFAULT_INVITATION_BODY = `Hello {recipientName},

You have been invited to provide feedback.

Your feedback is valuable and helps us improve our delivery process.`;

const DEFAULT_REMINDER_BODY = `Hello {recipientName},

This is a friendly reminder to complete your feedback.

Your input is important to us. It only takes a few minutes to complete.`;

const EmailComposeModal: React.FC<EmailComposeModalProps> = ({
  type,
  feedbackTitle,
  deadline,
  savedSubject,
  savedBody,
  onSend,
  onClose,
}) => {
  const defaultSubject = type === 'invitation'
    ? `You're invited to provide feedback: ${feedbackTitle}`
    : `Reminder: Please complete your feedback for ${feedbackTitle}`;

  const defaultBody = type === 'invitation' ? DEFAULT_INVITATION_BODY : DEFAULT_REMINDER_BODY;

  const [subject, setSubject] = useState(savedSubject || defaultSubject);
  const [body, setBody] = useState(savedBody || defaultBody);

  const handleSend = () => {
    onSend(subject, body);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              {type === 'invitation' ? (
                <Mail className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              ) : (
                <Send className="h-5 w-5 text-teal-600 dark:text-teal-400" />
              )}
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {type === 'invitation' ? 'Send Invitations' : 'Send Reminders'}
              </h2>
            </div>
          </div>

          {deadline && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Deadline: {new Date(deadline).toLocaleDateString()}
            </p>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Subject
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Message
              </label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={7}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white resize-vertical"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Use <code className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs">{'{recipientName}'}</code> to personalise for each recipient. A feedback link button is automatically appended.
              </p>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-full hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={!subject.trim() || !body.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Send Emails
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailComposeModal;
